import { useChatStore } from '../store/chatStore';
import type { StreamingState } from '../../features/chat/types';

interface WebSocketMessage {
    type: string;
    content: string;
    threadId?: string;
    chatId?: string;
}

interface MessageData {
    id: string;
    content: string;
    role: string;
    chat_id: string;
    created_at: string;
}

export class WebSocketService {
    private static instance: WebSocketService;
    private ws: WebSocket | null = null;
    private connectionPromise: Promise<void> | null = null;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;

    private constructor() {}

    static getInstance(): WebSocketService {
        if (!WebSocketService.instance) {
            WebSocketService.instance = new WebSocketService();
        }
        return WebSocketService.instance;
    }

    private handleMessage(event: MessageEvent) {
        try {
            const data = JSON.parse(event.data);
            console.log('[WebSocket] Received message:', data);
            
            const store = useChatStore.getState();
            const currentChatId = store.currentChat?.id;
            const currentThreadId = store.currentChat?.thread_id;
            
            // Check if message belongs to current chat's thread
            if (currentThreadId && data.thread_id === currentThreadId) {
                console.log(`[WebSocket] Processing message for thread ${currentThreadId}`);
                
                switch (data.type) {
                    case 'assistant_message':
                        // Parse the content JSON
                        console.log('[WebSocket] Received assistant_message, starting stream');
                        const content = JSON.parse(data.content);
                        useChatStore.setState({
                            isMessageLoading: false,
                            streaming: {
                                message: content.message,
                                questions: null,
                                verses: content.verses || null,
                                error: false
                            }
                        });
                        break;

                    case 'assistant_message_complete':
                        console.log('[WebSocket] Message complete, adding to messages');
                        const finalContent = JSON.parse(data.content);
                        const assistantMessage = {
                            id: 'temp-' + Date.now() + '-assistant',
                            content: data.content,
                            role: 'assistant',
                            chat_id: currentChatId,
                            created_at: new Date().toISOString()
                        };

                        useChatStore.setState({
                            messages: [...store.messages, assistantMessage],
                            isMessageLoading: false,
                            streaming: {
                                message: null,
                                questions: null,
                                verses: null,
                                error: false
                            }
                        });
                        break;

                    case 'questions':
                        // Use the last message from the store instead of streaming
                        const lastMessage = store.messages[store.messages.length - 1];
                        if (lastMessage && lastMessage.role === 'assistant') {
                            const questionsMessage = {
                                id: 'temp-' + Date.now() + '-questions',
                                content: data.content,
                                role: 'questions',
                                chat_id: currentChatId,
                                created_at: new Date().toISOString()
                            };

                            useChatStore.setState(state => ({
                                messages: [...state.messages, questionsMessage],
                                streaming: {
                                    message: null,
                                    questions: null,
                                    verses: null,
                                    error: false
                                },
                                hasGeneratedQuestions: true
                            }));
                        }
                        break;
                }
            } else {
                console.log(`[WebSocket] Ignoring message for thread ${data.thread_id}, current thread is ${currentThreadId}`);
            }
        } catch (error) {
            console.error('Error handling WebSocket message:', error);
        }
    }

    async connect(): Promise<void> {
        if (this.ws?.readyState === WebSocket.OPEN) {
            return;
        }

        if (this.connectionPromise) {
            return this.connectionPromise;
        }

        this.connectionPromise = new Promise((resolve, reject) => {
            try {
                const baseUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:3001';
                const wsUrl = new URL('/ws', baseUrl);
                this.ws = new WebSocket(wsUrl.toString());

                this.ws.onopen = () => {
                    console.log('WebSocket connected');
                    this.reconnectAttempts = 0;
                    this.connectionPromise = null;
                    resolve();
                };

                this.ws.onclose = () => {
                    console.log('WebSocket disconnected');
                    this.ws = null;
                    this.connectionPromise = null;
                    this.handleReconnect();
                };

                this.ws.onerror = (error) => {
                    console.error('WebSocket error:', error);
                    this.connectionPromise = null;
                    reject(error);
                };

                this.ws.onmessage = this.handleMessage.bind(this);
            } catch (error) {
                this.connectionPromise = null;
                reject(error);
            }
        });

        return this.connectionPromise;
    }

    private handleReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('Max reconnection attempts reached');
            return;
        }

        const backoffTime = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
        this.reconnectAttempts++;

        console.log(`Attempting to reconnect in ${backoffTime}ms...`);
        setTimeout(() => {
            this.connect().catch(error => {
                console.error('Reconnection failed:', error);
            });
        }, backoffTime);
    }

    disconnect() {
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        this.connectionPromise = null;
        this.reconnectAttempts = 0;
    }

    async send(message: WebSocketMessage) {
        await this.connect(); // Ensure connection is established
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
        } else {
            throw new Error('WebSocket is not connected');
        }
    }
}

// Export the singleton instance
export const websocketService = WebSocketService.getInstance();